/**
 * @fileoverview Content Plan Facebook Page publishing panel.
 * @module facebook-pages-connect/components/FacebookPagePublishPanel
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import { useFacebookPagesConnect } from '../hooks/useFacebookPagesConnect';
import type { FacebookPagesPublicationStatus } from '../types';

export interface FacebookPagePublishPanelProps {
  projectId: string;
  strategyId?: string;
  publicationId?: string;
  scheduledAt?: string;
  message?: string;
  link?: string;
  initialStatus?: FacebookPagesPublicationStatus | null;
  disabled?: boolean;
}

function formatDateTime(value?: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString();
}

function shouldSchedule(scheduledAt?: string): boolean {
  if (!scheduledAt) return false;
  const parsed = new Date(scheduledAt);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getTime() - Date.now() >= 10 * 60 * 1000;
}

function statusColor(status: FacebookPagesPublicationStatus['status']): 'success' | 'warning' | 'error' {
  if (status === 'published') return 'success';
  if (status === 'scheduled') return 'warning';
  return 'error';
}

export const FacebookPagePublishPanel: React.FC<FacebookPagePublishPanelProps> = ({
  projectId,
  strategyId,
  publicationId,
  scheduledAt,
  message,
  link,
  initialStatus,
  disabled = false,
}) => {
  const {
    status,
    pages,
    selectedPage,
    publishResult,
    isConnected,
    loading,
    actionLoading,
    pagesLoading,
    publishing,
    readbackLoading,
    error,
    connect,
    disconnect,
    refreshPages,
    selectPage,
    publish,
    readback,
  } = useFacebookPagesConnect(projectId);

  const [pageId, setPageId] = useState('');
  const [draftMessage, setDraftMessage] = useState(message || '');
  const [draftLink, setDraftLink] = useState(link || '');
  const effectiveResult = publishResult || initialStatus || null;
  const scheduleMode = shouldSchedule(scheduledAt);
  const scheduledLabel = formatDateTime(scheduledAt);
  const hasPublishPage = Boolean(selectedPage?.id || pageId);
  const hasPublishContent = Boolean(draftMessage.trim() || draftLink.trim());
  const canPublish = isConnected && hasPublishPage && hasPublishContent;
  const pageSelectionNeedsSave = Boolean(pageId && pageId !== selectedPage?.id);

  useEffect(() => {
    setDraftMessage(message || '');
  }, [message]);

  useEffect(() => {
    setDraftLink(link || '');
  }, [link]);

  useEffect(() => {
    if (selectedPage?.id) {
      setPageId(selectedPage.id);
      return;
    }
    if (!pageId && pages[0]?.id) {
      setPageId(pages[0].id);
    }
  }, [pageId, pages, selectedPage?.id]);

  const connectionChip = useMemo(() => {
    if (loading) return <Chip size="small" label="Loading" />;
    if (!isConnected) return <Chip size="small" color="default" label="Not connected" />;
    if (status?.needsReconnect) return <Chip size="small" color="warning" label="Reconnect required" />;
    return <Chip size="small" color="success" label="Connected" />;
  }, [isConnected, loading, status?.needsReconnect]);

  const handleSelectPage = async () => {
    if (!pageId) return;
    await selectPage(pageId);
  };

  const handlePublish = async () => {
    if (pageSelectionNeedsSave) {
      await selectPage(pageId);
    }
    await publish({
      strategyId,
      publicationId,
      message: draftMessage,
      link: draftLink,
      scheduledAt: scheduleMode ? scheduledAt : undefined,
    });
  };

  const handleReadback = async () => {
    if (!effectiveResult?.postId) return;
    await readback(effectiveResult.postId, { strategyId, publicationId });
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Facebook Pages for this project? You will need to reconnect before publishing again.')) {
      return;
    }
    await disconnect();
  };

  return (
    <Accordion
      variant="outlined"
      disableGutters
      data-testid={CONTENT_PLAN.FACEBOOK_PAGE_PUBLISH_PANEL}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        data-testid={CONTENT_PLAN.FACEBOOK_PAGE_PANEL_SUMMARY}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <FacebookIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2">Facebook</Typography>
          {connectionChip}
          {selectedPage?.name && (
            <Chip size="small" variant="outlined" label={selectedPage.name} />
          )}
          {effectiveResult?.status && (
            <Chip
              size="small"
              color={statusColor(effectiveResult.status)}
              label={effectiveResult.status}
            />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading Facebook connection</Typography>
            </Stack>
          ) : !isConnected ? (
            <Stack spacing={1}>
              <Button
                variant="contained"
                startIcon={<FacebookIcon />}
                onClick={connect}
                disabled={disabled || actionLoading}
                data-testid={CONTENT_PLAN.FACEBOOK_PAGE_CONNECT_BUTTON}
              >
                Connect Facebook
              </Button>
            </Stack>
          ) : (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                {status?.displayName && (
                  <Chip size="small" label={`Connected as ${status.displayName}`} />
                )}
                {(status?.scopes || []).map((scope) => (
                  <Chip key={scope} size="small" variant="outlined" label={scope} />
                ))}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel id="facebook-page-select-label">Page</InputLabel>
                  <Select
                    labelId="facebook-page-select-label"
                    label="Page"
                    value={pageId}
                    onChange={(event: SelectChangeEvent) => setPageId(event.target.value)}
                    disabled={disabled || pagesLoading || actionLoading}
                    data-testid={CONTENT_PLAN.FACEBOOK_PAGE_SELECT}
                  >
                    {pages.map((page) => (
                      <MenuItem key={page.id} value={page.id}>
                        {page.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={refreshPages}
                  disabled={disabled || pagesLoading}
                  data-testid={CONTENT_PLAN.FACEBOOK_PAGE_REFRESH_PAGES}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleSelectPage}
                  disabled={disabled || actionLoading || !pageId || pageId === selectedPage?.id}
                  data-testid={CONTENT_PLAN.FACEBOOK_PAGE_SAVE_SELECTION}
                >
                  Choose Page
                </Button>
              </Stack>

              <Divider />

              <TextField
                label="Post text"
                value={draftMessage}
                onChange={(event) => setDraftMessage(event.target.value)}
                multiline
                minRows={3}
                disabled={disabled || publishing}
                inputProps={{ 'data-testid': CONTENT_PLAN.FACEBOOK_PAGE_POST_MESSAGE }}
              />
              <TextField
                label="Link"
                value={draftLink}
                onChange={(event) => setDraftLink(event.target.value)}
                size="small"
                disabled={disabled || publishing}
                inputProps={{ 'data-testid': CONTENT_PLAN.FACEBOOK_PAGE_POST_LINK }}
              />
              {scheduledLabel && (
                <Typography variant="caption" color="text.secondary">
                  {scheduleMode ? 'Scheduled for ' : 'Calendar slot: '}{scheduledLabel}
                </Typography>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handlePublish}
                  disabled={disabled || actionLoading || publishing || !canPublish}
                  data-testid={CONTENT_PLAN.FACEBOOK_PAGE_PUBLISH_BUTTON}
                >
                  {publishing ? 'Publishing' : scheduleMode ? 'Schedule on Facebook' : 'Publish to Facebook'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReadback}
                  disabled={disabled || readbackLoading || !effectiveResult?.postId}
                  data-testid={CONTENT_PLAN.FACEBOOK_PAGE_READBACK_BUTTON}
                >
                  Check status
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={handleDisconnect}
                  disabled={disabled || actionLoading}
                  data-testid={CONTENT_PLAN.FACEBOOK_PAGE_DISCONNECT_BUTTON}
                >
                  Disconnect
                </Button>
              </Stack>

              {effectiveResult && (
                <Alert
                  severity={effectiveResult.status === 'failed' ? 'error' : 'success'}
                  data-testid={CONTENT_PLAN.FACEBOOK_PAGE_PUBLISH_RESULT}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      {effectiveResult.status}
                      {effectiveResult.postId ? ` · ${effectiveResult.postId}` : ''}
                    </Typography>
                    {effectiveResult.permalink && (
                      <Box>
                        <Link
                          href={effectiveResult.permalink}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={CONTENT_PLAN.FACEBOOK_PAGE_RESULT_PERMALINK}
                        >
                          <LinkIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                          Open post
                        </Link>
                      </Box>
                    )}
                    {effectiveResult.error && (
                      <Typography variant="caption">{effectiveResult.error}</Typography>
                    )}
                  </Stack>
                </Alert>
              )}
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default FacebookPagePublishPanel;
